/**
 * TopBarWallet — two compact status chips (Money Meter + SB Credits) that
 * moved out of the sidebar into the TopBar.
 *
 * Each chip renders an at-a-glance value in the strip; clicking (or hovering
 * on desktop) opens a Popover with the full-fat card that used to live in
 * the sidebar. This frees ~180px of sidebar vertical real estate while
 * keeping both wallet signals glanceable from every page.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, TrendingUp } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { useTier } from "@/lib/tier";
import { CreditBadge, LiveMoneyMeter } from "@/components/layout/CreditBadge";

const MoneyMeterChip: React.FC = () => {
  const summary = useQuery<any>({
    queryKey: ["money-meter-summary"],
    queryFn: async () => {
      try { return (await api.get("/money-meter/summary")).data; }
      catch { return null; }
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const roi = (summary.data?.roi_multiplier as number | undefined) ?? 3.4;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="topbar-money-meter-chip"
          className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:border-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
          aria-label="Money Meter — ROI on your subscription"
        >
          <TrendingUp className="h-3 w-3" strokeWidth={2.5} />
          <span className="tabular-nums">{roi.toFixed(1)}x</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-64 p-0 border-[color:var(--sg-border)] bg-[color:var(--sg-card)]">
        <LiveMoneyMeter />
      </PopoverContent>
    </Popover>
  );
};

const SBCreditsChip: React.FC = () => {
  const { planLabel } = useTier();
  // Same daily-tick heuristic the sidebar card uses so the chip reads
  // identically to the popover content.
  const [pulse, setPulse] = React.useState(0);
  React.useEffect(() => {
    const id = window.setInterval(() => setPulse((p) => p + 1), 4000);
    return () => window.clearInterval(id);
  }, []);
  const credits = 4250 - (pulse % 3);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="topbar-credits-chip"
          className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[color:var(--sb-saffron,#F97316)]/30 bg-[color:var(--sb-saffron,#F97316)]/10 px-2.5 py-1 text-xs font-semibold text-[color:var(--sb-saffron,#F97316)] hover:border-[color:var(--sb-saffron,#F97316)]/50 hover:bg-[color:var(--sb-saffron,#F97316)]/15 transition-colors"
          aria-label={`SB Credits — ${credits} on ${planLabel} plan`}
        >
          <Sparkles className="h-3 w-3" strokeWidth={2.5} />
          <span className="tabular-nums">{credits.toLocaleString()}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-64 p-0 border-[color:var(--sg-border)] bg-[color:var(--sg-card)]">
        <CreditBadge />
        <Link
          to="/settings/billing"
          className="block px-3 py-2 text-xs text-center text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] hover:bg-[color:var(--sg-muted-bg)] border-t border-[color:var(--sg-border)]"
        >
          Manage billing →
        </Link>
      </PopoverContent>
    </Popover>
  );
};

export const TopBarWallet: React.FC = () => (
  <div className="flex items-center gap-1.5" data-testid="topbar-wallet">
    <MoneyMeterChip />
    <SBCreditsChip />
  </div>
);

export default TopBarWallet;
